import { Checkbox, Fieldset } from '@ark-ui/react';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';

import styles from './CheckboxInput.module.scss';

const CheckboxItem = (props: Checkbox.RootProps & { className?: string }) => {
  const { className, ...otherProps } = props;
  return (
    <Checkbox.Root className={cn(styles['checkbox-item'], className)} {...otherProps}>
      <Checkbox.Control className={styles.control}>
        <Checkbox.Indicator className={styles.indicator} />
      </Checkbox.Control>
      <Checkbox.Label className={'body-sm'}>{props.children}</Checkbox.Label>
      <Checkbox.HiddenInput />
    </Checkbox.Root>
  );
};

export type CheckboxInputProps = {
  label: string;
  options: Array<{ label: string }>;
  required: boolean;
  className?: string;
};

const CheckboxInput = (props: CheckboxInputProps): JSX.Element => {
  const { label, options, required, className } = props;
  const { selectAllLabel } = useDictionary();
  const ref = useRef<HTMLFieldSetElement>(null);

  const items = options.map((option) => ({
    label: option.label,
    value: option.label,
  }));

  const [value, setValue] = useState<string[]>([]);

  const handleSelectAll = (checked: boolean) => {
    setValue(checked ? items.map((item) => item.value) : []);
  };

  const allSelected = value.length === items.length;

  useEffect(() => {
    if (required && ref.current) {
      const firstInput = ref.current.querySelector<HTMLInputElement>('input[type="checkbox"]');
      firstInput?.setCustomValidity(value.length === 0 ? 'Please select at least one option.' : '');
    }
  }, [required, value]);

  return (
    <Fieldset.Root className={cn(styles.field, className)} ref={ref}>
      <Fieldset.Legend className={cn(styles['field-legend'], 'body-eyebrow')}>
        {label}
        {required && '*'}
      </Fieldset.Legend>
      <CheckboxItem
        value="all"
        checked={allSelected}
        className={styles['select-all']}
        onCheckedChange={(e) => handleSelectAll(!!e.checked)}
      >
        {selectAllLabel}
      </CheckboxItem>

      <Checkbox.Group className={styles.group} value={value} name={label} onValueChange={setValue}>
        {items.map((item) => (
          <CheckboxItem value={item.value} key={item.value} className={styles['checkbox-item']}>
            {item.label}
          </CheckboxItem>
        ))}
      </Checkbox.Group>
    </Fieldset.Root>
  );
};

export default CheckboxInput;
